import { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { addMonths, differenceInCalendarMonths } from 'date-fns';
import classNames from 'classnames';

import { trans } from '@spotahome/soyuz/client';

import {
  formatMonthlyPaymentDate,
  isSameMonth,
  isInRangeMonth,
  setFirstDateOfMonth
} from '../../utils/dateUtils';
import { dateIsBefore, dateIsBetween } from '../../utils/DateRangeHandler';

import DateFilterHeader from '../DateFilterHeader';

import styles from './MonthlyFilter.module.scss';

const MAX_MONTHS = 24;

const isCurrentMonth = (month, todayMonth) => {
  return isSameMonth(month, todayMonth);
};

const isSelectedMonth = (month, from, to) => {
  return isSameMonth(month, from) || isSameMonth(month, to);
};

const getMonthArray = (todayMonth, from, to, hoveredMonth, minMonthStay) => {
  const startDate = from ? setFirstDateOfMonth(from) : todayMonth;
  const extraMonthsToAdd = differenceInCalendarMonths(startDate, todayMonth);
  const minToMonth =
    from && minMonthStay ? addMonths(from, minMonthStay - 1) : null;

  return Array.from({ length: MAX_MONTHS + extraMonthsToAdd }, (_, i) => {
    const month = addMonths(todayMonth, i);
    const isSelected = isSelectedMonth(month, from, to);

    return {
      month,
      isSelected,
      isCurrent: isCurrentMonth(month, todayMonth),
      isInRange:
        (isInRangeMonth(month, from, to) ||
          isInRangeMonth(month, from, hoveredMonth)) &&
        !isSelected,
      isDisabled: minToMonth ? dateIsBetween(month, from, minToMonth) : false
    };
  });
};

const MonthlyFilter = ({
  from,
  to,
  onDatesChange,
  minMonthStay,
  onFilterSwitcherClick
}) => {
  const [isSelectingStartRange, setIsSelectingStartRange] = useState(!from);
  const [hoveredMonth, setHoveredMonth] = useState(null);
  const todayMonth = setFirstDateOfMonth(Date.now());

  const handleSelectMonth = month => {
    if (isSelectingStartRange) {
      onDatesChange({
        moveIn: month
      });
      setIsSelectingStartRange(false);
    } else if (dateIsBefore(month, from)) {
      onDatesChange({
        moveIn: month
      });
    } else {
      onDatesChange({
        moveOut: month
      });
    }
  };

  useEffect(() => {
    if (!from && !to) {
      setIsSelectingStartRange(true);
    }
  }, [from, to]);

  return (
    <>
      <DateFilterHeader
        isMonthsFilter
        onClickSwitcher={onFilterSwitcherClick}
        subtitle={
          <p>
            {trans('search.exposedfilters.dates.monthly.subtitle', {
              nMonths: minMonthStay
            })}
          </p>
        }
      />

      <div className={styles['monthly-filter']}>
        {getMonthArray(todayMonth, from, to, hoveredMonth, minMonthStay).map(
          ({ month, isCurrent, isSelected, isInRange, isDisabled }) => (
            <div
              className={styles['monthly-filter__month-wrapper']}
              key={formatMonthlyPaymentDate(month)}
              {...(isDisabled
                ? {}
                : {
                    role: 'button',
                    tabIndex: 0,
                    onClick: () => handleSelectMonth(month),
                    onMouseEnter: () => setHoveredMonth(month),
                    onMouseLeave: () => setHoveredMonth(null)
                  })}
            >
              <div
                className={classNames(styles['monthly-filter__month'], {
                  [styles['monthly-filter__month--current']]: isCurrent,
                  [styles['monthly-filter__month--selected']]: isSelected,
                  [styles['monthly-filter__month--in-range']]: isInRange,
                  [styles['monthly-filter__month--disabled']]: isDisabled
                })}
              >
                {formatMonthlyPaymentDate(month)}
              </div>
            </div>
          )
        )}
      </div>
    </>
  );
};

MonthlyFilter.propTypes = {
  from: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  onDatesChange: PropTypes.func.isRequired,
  minMonthStay: PropTypes.number.isRequired,
  onFilterSwitcherClick: PropTypes.func.isRequired
};

export default memo(MonthlyFilter, (prevProps, nextProps) => {
  return prevProps.from === nextProps.from && prevProps.to === nextProps.to;
});
